import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Chip,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";

export default function BillingPage({ token }) {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock invoices (can be replaced with real API call later)
  const invoicesSeed = useMemo(
    () => [
      { id: "INV-1001", amount: 19.99, status: "Paid", date: "2025-06-01" },
      { id: "INV-1002", amount: 19.99, status: "Paid", date: "2025-07-01" },
      { id: "INV-1003", amount: 19.99, status: "Paid", date: "2025-08-01" },
      { id: "INV-1004", amount: 19.99, status: "Open", date: "2025-09-01" },
    ],
    []
  );

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const [statusRes, plansRes] = await Promise.all([
          axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/subscription/status`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/subscription/plans`),
        ]);
        const subData = statusRes.data.subscription;
        const plansData = plansRes.data.plans || [];
        setPlans(plansData);
        
        if (subData) {
          const plan = plansData.find((p) => p._id === subData.planId);
          setSubscription({
            plan: plan?.name || subData.planId || "Unknown",
            price: plan?.price || 0,
            status: subData.status || "Unknown",
            renewsOn: subData.endDate
              ? new Date(subData.endDate).toLocaleDateString()
              : "N/A",
            planId: subData.planId,
            subscriptionId: subData.id,
          });
        }
      } catch (err) {
        console.error("Error fetching subscription data:", err);
        setError("Failed to load subscription data");
      } finally {
        setLoading(false);
      }
    };
    fetchSubscriptionData();
  }, [token]);

  const handleChangePlan = () => {
    navigate("/pricing");
  };

  const handleCancel = async () => {
    if (!token || !subscription) return;
    if (
      !window.confirm(
        "Are you sure you want to cancel your subscription? This action cannot be undone."
      )
    ) {
      return;
    }
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.reload();
    } catch (err) {
      console.error("Error canceling subscription:", err);
      alert("Failed to cancel subscription. Please try again.");
    }
  };

  const handleUpdatePaymentMethod = () => {
    // TODO: Implement payment method update
    alert("Payment method update coming soon!");
  };

  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const invoices = useMemo(() => invoicesSeed, [invoicesSeed]);

  const statusChip = (s) => (
    <Chip
      label={s}
      color={s === "Paid" ? "success" : s === "Open" ? "warning" : "default"}
      size="small"
      variant="outlined"
    />
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return invoices.filter((inv) => {
      const matchesQ = !q || inv.id.toLowerCase().includes(q);
      const matchesS = statusFilter === "All" || inv.status === statusFilter;
      return matchesQ && matchesS;
    });
  }, [invoices, query, statusFilter]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const hasActiveSubscription = subscription && subscription.status === "active";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <div className="mt-4 mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
        <Typography className="text-sm text-red-800 dark:text-red-200">
          ⚠️ {error}
        </Typography>
      </div>
    );
  }

  return (
    <Box>
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <CreditCardIcon className="text-primary-600 dark:text-primary-400" />
        <Typography variant="h6" className="text-2xl font-bold text-gray-900 dark:text-white">
          Billing
        </Typography>
      </div>

      {/* Subscription summary */}
      <div className="card-modern p-6 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-3">
            <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Current Plan
            </Typography>
            <Typography variant="h5" className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {subscription?.plan || "No Plan"}
            </Typography>
            <Typography variant="body2" className="text-sm text-gray-500 dark:text-gray-400">
              {subscription?.price
                ? `₹${subscription.price.toFixed(2)}/mo`
                : "N/A"}
            </Typography>
          </div>
          <div className="md:col-span-3">
            <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Status
            </Typography>
            <Box>
              {subscription
                ? statusChip(subscription.status)
                : statusChip("No Subscription")}
            </Box>
          </div>
          <div className="md:col-span-3">
            <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Renews On
            </Typography>
            <Typography variant="body1" className="text-gray-900 dark:text-white">
              {subscription?.renewsOn || "N/A"}
            </Typography>
          </div>
          <div className="md:col-span-3 flex flex-col sm:flex-row gap-2 justify-end">
            <Button
              variant="outlined"
              disabled={!hasActiveSubscription}
              onClick={handleChangePlan}
              className="border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 normal-case"
            >
              Change Plan
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={!hasActiveSubscription}
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700 text-white normal-case"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>

      {/* Invoices */}
      <div className="card-modern p-4 mt-6">
        <div className="flex items-center justify-between mb-4">
          <Typography variant="subtitle1" className="text-lg font-bold text-gray-900 dark:text-white">
            Invoices
          </Typography>
          <Button
            variant="contained"
            disabled={!hasActiveSubscription}
            onClick={handleUpdatePaymentMethod}
            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
          >
            Update Payment Method
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-4 mb-4">
          <div className="md:col-span-3">
            <TextField
              fullWidth
              size="small"
              label="Search Invoice ID"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              className="bg-white dark:bg-gray-800"
            />
          </div>
          <div className="md:col-span-2">
            <FormControl fullWidth size="small">
              <InputLabel id="inv-status-label" className="text-gray-700 dark:text-gray-300">Status</InputLabel>
              <Select
                labelId="inv-status-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                className="bg-white dark:bg-gray-800"
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Open">Open</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>

        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Invoice</TableCell>
              <TableCell align="right" className="text-gray-700 dark:text-gray-300 font-semibold">Amount</TableCell>
              <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Status</TableCell>
              <TableCell className="text-gray-700 dark:text-gray-300 font-semibold">Date</TableCell>
              <TableCell align="right" className="text-gray-700 dark:text-gray-300 font-semibold">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((inv) => (
              <TableRow key={inv.id} hover>
                <TableCell className="text-gray-900 dark:text-white">{inv.id}</TableCell>
                <TableCell align="right" className="text-gray-900 dark:text-white font-medium">₹{inv.amount.toFixed(2)}</TableCell>
                <TableCell>{statusChip(inv.status)}</TableCell>
                <TableCell className="text-gray-700 dark:text-gray-300">{inv.date}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="text" disabled className="text-gray-500 dark:text-gray-400 normal-case">
                    Download
                  </Button>
                  <Button size="small" variant="text" disabled className="text-gray-500 dark:text-gray-400 normal-case">
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="mt-4">
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            className="text-gray-700 dark:text-gray-300"
          />
        </div>
      </div>
    </Box>
  );
}
